import { jsx as _jsx } from "react/jsx-runtime";
import { format as dateFnsFormat } from "date-fns";
import fastDeepEqual from "fast-deep-equal/react.js";
import ConfirmDate from "flatpickr/dist/plugins/confirmDate/confirmDate.js";
import { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";
import ReactFlatpickr from "react-flatpickr";
import useIsDesktop from "../../../../hooks/device/useIsDesktop.js";
import { l10nExtended } from "./types.js";
import { parseEvents } from "./utils.js";
// @ts-ignore
const Calendar = ReactFlatpickr.default ? ReactFlatpickr.default : ReactFlatpickr;
/* Draw direct flight span */ const drawDirectFlightDay = (flightDays, actualDay, elementDay)=>{
    const hasDirectFlight = flightDays.dates.some((item)=>item === actualDay);
    if (hasDirectFlight) {
        elementDay.innerHTML += `<span class='${flightDays.class}'></span>`;
    }
};
const drawOfferDay = (offersDays, actualDay, elementDay, locale)=>{
    const offer = offersDays?.dates.find((date)=>date.date === actualDay);
    if (offer) {
        elementDay.innerHTML += `<span class='flatpickr-day-price'>${parseFloat(offer.text).toLocaleString(locale)}${offer.currency}</span>`;
        if (offer.offer) {
            elementDay.className += ' is-offer';
        }
    }
};
const drawRestrictedDays = (disabled, actualDay, elementDay)=>{
    const disabledDay = disabled?.find((date)=>date === actualDay);
    if (disabledDay) {
        elementDay.className += ' is-disabled';
    }
};
export const FlatpickrCalendar = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { locale ='es' , options ={} , events =[] , onChange , onReady , onDayCreate , onClose , enableDates =[] , className ='' , value , restrictedDays , ...rest } = props;
    const { plugins =[]  } = options || {};
    const isDesktop = useIsDesktop();
    const calendarPlugins = useMemo(()=>options.enableTime ? [
            ConfirmDate({
                confirmIcon: '',
                confirmText: 'OK'
            }),
            ...plugins
        ] : [
            ...plugins
        ], [
        options.enableTime,
        plugins
    ]);
    const calendarOptions = useMemo(()=>({
            locale: {
                ...l10nExtended[locale],
                rangeSeparator: ' → '
            },
            plugins: calendarPlugins,
            ...options
        }), [
        calendarPlugins,
        locale,
        options
    ]);
    const parsedEvents = useMemo(()=>parseEvents(events), [
        events
    ]);
    const lastValueRef = useRef(value);
    const flatpickrInstance = useRef();
    const handleOnReady = useCallback((dates, currentDateString, self, data)=>{
        flatpickrInstance.current = self;
        for (const event of parsedEvents){
            const calendarFooter = document.createElement('div');
            const calendarFooterParagraph = document.createElement('p');
            calendarFooter.appendChild(calendarFooterParagraph);
            calendarFooterParagraph.innerText = event.legend.text;
            calendarFooter.classList.add('flatpickr-day-footer');
            calendarFooterParagraph.classList.add(event.legend.class);
            if (event.legend.subtitle) {
                const calendarFooterSubtitle = document.createElement('p');
                calendarFooterSubtitle.innerText = event.legend.subtitle;
                calendarFooterSubtitle.classList.add('flatpickr-day-footer-subtitle');
                calendarFooter.appendChild(calendarFooterSubtitle);
            }
            self.calendarContainer.appendChild(calendarFooter);
        }
        if (className && className.length > 0) self.calendarContainer?.classList?.add(className);
        if (onReady) onReady(dates, currentDateString, self, data);
    }, [
        onReady,
        parsedEvents,
        className
    ]);
    const { date =false , offers =false  } = parsedEvents[0] || {};
    const enabledDays = enableDates;
    const handleOnDayCreate = useCallback((dates, currentDateString, self, data)=>{
        const actualDate = new Date(data.dateObj);
        const actualDay = dateFnsFormat(actualDate, 'yyyy-MM-dd');
        if (date) drawDirectFlightDay(date, actualDay, data);
        if (offers) drawOfferDay(offers, actualDay, data, locale);
        if (restrictedDays?.length) drawRestrictedDays(restrictedDays, actualDay, data);
        // disable date if it is not in enableDates array
        if (enabledDays.length > 0 && !enabledDays.includes(actualDay)) {
            data.className += ' flatpickr-disabled';
        }
        onDayCreate?.(dates, currentDateString, self, data);
    }, [
        date,
        offers,
        enabledDays,
        locale,
        restrictedDays,
        onDayCreate
    ]);
    const handleOnClose = useCallback((dates, currentDateString, self, data)=>{
        if (options.mode === 'range') {
            if (dates.length !== 2 && lastValueRef.current) {
                // push previous dates
                if (onChange) onChange(lastValueRef.current, currentDateString, self, data);
            } else {
                lastValueRef.current = dates;
            }
        }
        if (onClose) onClose(dates, currentDateString, self, data);
    }, [
        onChange,
        onClose,
        options.mode
    ]);
    useEffect(()=>{
        if (value?.length === 2 && !fastDeepEqual(value, lastValueRef.current)) {
            lastValueRef.current = value;
        }
        const selectedDates = flatpickrInstance.current?.selectedDates;
        const isSingle = flatpickrInstance.current?.config.mode === 'single';
        if (!!value?.length && !selectedDates?.length) {
            flatpickrInstance.current?.setDate(value);
            return;
        }
        if (!value?.length || !selectedDates?.length) return;
        const isDifferentValue = dateFnsFormat(value[0], 'yyyy-MM-dd') !== dateFnsFormat(selectedDates[0], 'yyyy-MM-dd');
        const isDiffDate = isSingle ? isDifferentValue : isDifferentValue || value.length == 2 && dateFnsFormat(value[1], 'yyyy-MM-dd') !== dateFnsFormat(selectedDates[1], 'yyyy-MM-dd');
        if (isDiffDate) {
            flatpickrInstance.current?.setDate(value);
        }
    }, [
        value
    ]);
    useEffect(()=>{
        /* Update rendered months */ if (flatpickrInstance.current?.isOpen && isDesktop) {
            const currDate = new Date();
            flatpickrInstance.current?.changeMonth(currDate.getMonth(), false);
            flatpickrInstance.current?.changeYear(currDate.getFullYear());
            if (value?.[0]) flatpickrInstance.current?.jumpToDate(value[0]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        flatpickrInstance.current?.isOpen
    ]);
    return /*#__PURE__*/ _jsx(Calendar, {
        "data-testid": "FlatpickrCalendar",
        options: calendarOptions,
        onReady: handleOnReady,
        onClose: handleOnClose,
        onDayCreate: handleOnDayCreate,
        ref: ref,
        onChange: onChange,
        ...rest
    });
});
FlatpickrCalendar.displayName = 'FlatpickrCalendar';
export default FlatpickrCalendar;

//# sourceMappingURL=index.js.map